import chalk from 'chalk';
import {Command} from 'commander';
import ora from 'ora';
import prompts from 'prompts';
import * as z from 'zod';

import {cloneBlankTemplate, cloneKreateTemplate} from '@/utilities/clone';
import {handleError} from '@/utilities/errors';
import {directoryExists, joinPath} from '@/utilities/files';
import {updateKogTemplate} from '@/utilities/kog';
import {logger} from '@/utilities/logger';

const initOptionsSchema = z.object({
  cwd: z.string(),
  override: z.boolean(),
  template: z.string(),
});

export type InitOptions = z.infer<typeof initOptionsSchema>;

export const init = new Command()
  .name('init')
  .description('initialize your project using a Kreate template')
  .argument('[projectName]', 'the name of the project')
  .option('-c, --cwd <cwd>', 'the working directory. defaults to the current directory.', process.cwd())
  .option('-o, --override', 'override existing files.', false)
  .option('-t, --template <template>', 'the template to use to initiate the project if not already implemented.', '')
  .action(async (optionProjectName: string, opts: InitOptions) => {
    try {
      // Get parameters from command.
      const options = initOptionsSchema.parse(opts);
      const {override: optionOverride, cwd: optionCurrentWorkingDirectory, template: optionTemplate} = options;

      const spinner = ora();

      logger.info('');
      logger.info(`${chalk.green('KOG')} - a CLI tool to ${chalk.green('init')} KOG into your project.`);
      logger.info('');

      // Ask user what the name of the project is, if not set in argument.
      const {projectName} = optionProjectName
        ? {projectName: optionProjectName}
        : await prompts({
            type: 'text',
            name: 'projectName',
            message: 'What is the name of the project?',
          });

      const currentWorkingDirectory = joinPath(optionCurrentWorkingDirectory, projectName);

      // If the working directory doesn't exist...
      if (!optionOverride && (await directoryExists(currentWorkingDirectory))) {
        spinner.start().fail(`Installing template - Skipped as it already exists. To override, run with the ${chalk.green('--override')} flag.`);
      } else {
        // Ask user if they would like to create the working directory from a template.
        const {template} = optionTemplate
          ? {template: optionTemplate}
          : await prompts({
              type: 'select',
              name: 'template',
              message: `The path "${logger.highlight(currentWorkingDirectory)}" does not exist. Which ${logger.highlight(
                'template'
              )} would you like to use to create it?`,
              choices: [
                {title: 'Kreate', value: 'kreate', description: `Custom D's NextJS template with Mui, typescript, eslint, prettier, and more.`},
                {title: 'Blank', value: 'blank', description: `A blank project with nothing more than a README.md`},
                {title: 'Cancel', value: 'cancel'},
              ],
              initial: 0,
            });

        spinner.start('Installing template');

        // Write Kreate template to current working directory.
        if (template === 'kreate') {
          spinner.text = `Installing "${template}" template`;
          await cloneKreateTemplate(currentWorkingDirectory);
        }

        // Write blank template to current working directory.
        if (template === 'blank') {
          spinner.text = `Installing "${template}" template`;
          await cloneBlankTemplate(currentWorkingDirectory);
        }

        spinner.succeed(`Installed "${template}" template`);
      }

      // Check if the working directory exists.
      if ((await directoryExists(currentWorkingDirectory)) === false) {
        spinner.start().fail(`Validating working directory - ${currentWorkingDirectory} does not exist. Please try again.`);
        process.exit(101);
      }

      // Install or update local version of KOG.
      spinner.start('Installing KOG');
      await updateKogTemplate(spinner);
      spinner.succeed('Installed KOG');
    } catch (error) {
      handleError(error);
    }
  });
